import {
  Actor,
  deterministicUuid,
  StepDefinitionId,
  StepInstanceId,
  WILLOW_INTERNAL_ACTOR,
  WorkflowInstanceId,
} from '@willow/types-iso';

import { StepDefinition, StepType } from '../definitions/steps/StepDefinition';
import { SerializedStepInstance } from './SerializedStepInstance';
import { SerializedWorkflowInstance } from './SerializedWorkflowInstance';

export interface ReconciledStep {
  serialized: SerializedStepInstance<StepType>;
  definition: StepDefinition;
}

export interface ReconciledSteps {
  steps: ReconciledStep[];
  orphans: SerializedStepInstance<StepType>[];
  backfilled: boolean;
}

export const reconcileSteps = (
  instance: SerializedWorkflowInstance,
  stepDefinitions: StepDefinition[],
  actor: Actor = WILLOW_INTERNAL_ACTOR,
): ReconciledSteps => {
  const stored = instance.serializedSteps;
  // Rows stored before the field existed lack it even though the type says it is required.
  if (stored.some((step) => step.stepDefinitionId == null)) {
    throw new Error(
      `Workflow instance ${instance.id} has a step without a stepDefinitionId; run the stamp migration (pnpm afterPull) before reading it`,
    );
  }
  const byDefinitionId = new Map(stored.map((step) => [step.stepDefinitionId, step]));

  const steps = stepDefinitions.map((definition): ReconciledStep => {
    const candidate = byDefinitionId.get(definition.id);
    if (candidate == null) {
      return { serialized: compileSerializedStep(instance, definition, actor), definition };
    }
    if (candidate.type !== definition.type) {
      throw new Error(
        `Workflow instance ${instance.id} stores step ${definition.id} as ${candidate.type} but definition ${instance.definitionId} declares it as ${definition.type}; a step that changes type needs a new id`,
      );
    }
    return { serialized: candidate, definition };
  });
  const matched = new Set(steps.map((step) => step.serialized));

  return {
    steps,
    orphans: stored.filter((step) => !matched.has(step)),
    backfilled: stepDefinitions.some((definition) => !byDefinitionId.has(definition.id)),
  };
};

// A step compiled during a read reaches the UI before anything persists it, and the UI mutates it by
// id, so every read must mint the same id. A random id would dangle as soon as the page re-fetched.
export const stableStepInstanceId = (
  workflowInstanceId: WorkflowInstanceId,
  stepDefinitionId: StepDefinitionId,
): StepInstanceId => deterministicUuid(`workflow-step:${workflowInstanceId}:${stepDefinitionId}`) as StepInstanceId;

export const compileSerializedStep = (
  instance: Pick<SerializedWorkflowInstance, 'id' | 'launchTime'>,
  stepDefinition: StepDefinition,
  actor: Actor,
): SerializedStepInstance<StepType> => ({
  id: stableStepInstanceId(instance.id, stepDefinition.id),
  stepDefinitionId: stepDefinition.id,
  type: stepDefinition.type,
  displayInfo: [],
  statusHistory: [
    {
      status: 'inactive',
      timestamp: instance.launchTime,
      actor,
      manual: false,
    },
  ],
  formSubmissions: [],
  retryCondition: undefined,
  dueAt: { type: 'dueUnset' },
  isVisible: false,
  canManuallyActivate: false,
  canManuallySkip: false,
  canManuallyComplete: false,
  canManuallyFail: false,
  canEditAfterCompletion: false,
  willActivateOn: undefined,
  actionsToTake: [],

  assignmentHistory: [],

  notes: [],
});
